import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Paper, 
    Typography, 
    List, 
    ListItem, 
    ListItemText,
    CircularProgress,
    Box,
    Chip,
    TextField,
    InputAdornment,
    IconButton,
    Alert,
    useTheme,
    useMediaQuery,
    Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import authApi from '../services/authApi';
import { sharePointService } from '../services/sharePointService';
import { useUserSession } from '../contexts/UserContext';
import { useSharePoint } from '../contexts/SharePointContext';
import NavigationDrawer from './Navigation/NavigationDrawer';
import { useNavigate } from 'react-router-dom';

const LOG_POLL_MS = 60_000;

function buildEtlLogsWebSocketUrl(configId) {
    if (process.env.REACT_APP_WS_URL) {
        const baseUrl = process.env.REACT_APP_WS_URL.replace(/\/$/, '');
        return `${baseUrl}/ws?configId=${configId}`;
    }
    const apiBase = process.env.REACT_APP_API_URL;
    if (apiBase) {
        try {
            const u = new URL(apiBase);
            const wsProtocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
            return `${wsProtocol}//${u.host}/ws?configId=${configId}`;
        } catch (_) {
            /* fall through */
        }
    }
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = window.location.hostname === 'localhost' ? 'localhost:5000' : window.location.host;
    return `${wsProtocol}//${wsHost}/ws?configId=${configId}`;
}

function ETLMonitoring() {
    const [logs, setLogs] = useState([]);
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);
    const [wsWarning, setWsWarning] = useState(null);
    const { userSession } = useUserSession();
    const isSystemAdmin = (userSession?.roles || []).includes('System Admin');
    const { effectiveConfigId } = useSharePoint();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const navigate = useNavigate();

    // Function to format the timestamp (now just returns the DB timestamp)
    const formatTimestamp = (timestamp) => {
        return timestamp;
    };

    // Function to get log level color
    const getLogLevelColor = (level) => {
        switch (level.toLowerCase()) {
            case 'error':
                return 'error';
            case 'warning':
                return 'warning';
            case 'info':
                return 'primary';
            case 'success':
                return 'success';
            default:
                return 'default';
        }
    };

    // Filter logs based on search term
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredLogs(logs);
            return;
        }

        const filtered = logs.filter(log => 
            log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.log_level.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredLogs(filtered);
    }, [logs, searchTerm]);

    // Fetch logs and optionally subscribe to live updates via WebSocket
    useEffect(() => {
        let ws = null;
        let pollTimer = null;
        let wsIntentionalClose = false;
        let cancelled = false;

        const fetchLogs = async () => {
            try {
                if (!userSession?.email) {
                    setError('User session not available.');
                    setLoading(false);
                    return;
                }

                setLoading(true);
                setError(null);
                const token = authApi.getToken();
                const response = await sharePointService.getETLLogs(token);
                if (!cancelled) {
                    setLogs(response);
                }
            } catch (fetchError) {
                console.error('Error fetching ETL logs:', fetchError);
                if (!cancelled) {
                    const errorMessage = fetchError.response?.data?.message || fetchError.message || 'Failed to load logs';
                    setError(errorMessage);
                    setLogs([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        const setupWebSocket = async () => {
            try {
                const token = authApi.getToken();
                const config = await sharePointService.getConfig(token);

                if (!config || config.success === false || !config.configs || config.configs.length === 0) {
                    setWsWarning('No SharePoint configuration found — live log streaming is unavailable. Logs refresh every minute.');
                    return;
                }

                const configId = effectiveConfigId || config.configs[0]?.id;
                const wsUrl = buildEtlLogsWebSocketUrl(configId);

                console.log('Connecting to WebSocket:', wsUrl);
                ws = new WebSocket(wsUrl);

                ws.onmessage = (event) => {
                    const newLog = JSON.parse(event.data);
                    setLogs((prevLogs) => [newLog, ...prevLogs]);
                    setWsWarning(null);
                };

                ws.onopen = () => {
                    console.log('WebSocket connected successfully');
                    setWsWarning(null);
                    if (pollTimer) {
                        clearInterval(pollTimer);
                        pollTimer = null;
                    }
                };

                ws.onerror = () => {
                    console.error('WebSocket error');
                    setWsWarning('Live log streaming unavailable. Logs refresh every minute.');
                };

                ws.onclose = (event) => {
                    console.log('WebSocket connection closed:', event.code, event.reason);
                    if (wsIntentionalClose || event.code === 1000) return;
                    setWsWarning('Live log streaming unavailable. Logs refresh every minute.');
                    if (!pollTimer) {
                        pollTimer = setInterval(fetchLogs, LOG_POLL_MS);
                    }
                };
            } catch (wsError) {
                console.error('WebSocket setup error:', wsError);
                setWsWarning('Live log streaming unavailable. Logs refresh every minute.');
            }
        };

        if (userSession?.effectiveEmail || userSession?.email) {
            fetchLogs().then(() => {
                if (!cancelled) {
                    setupWebSocket();
                }
            });
        } else {
            setLoading(false);
            setError('User session not available.');
        }

        return () => {
            cancelled = true;
            wsIntentionalClose = true;
            if (pollTimer) clearInterval(pollTimer);
            if (ws && ws.readyState === WebSocket.OPEN) {
                ws.close(1000);
            }
        };
    }, [userSession, effectiveConfigId]);

    const handleSearchClear = () => {
        setSearchTerm('');
    };

    const renderLogEntry = (log, index) => {
        // Create a unique key combining multiple properties
        const uniqueKey = `${log.created_at}-${log.log_level}-${index}`;
        
        // Format the timestamp to show EST time directly
        const timestamp = new Date(log.created_at).toLocaleString('en-US', {
            timeZone: 'America/New_York',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        }) + ' EST';

        return (
            <ListItem 
                key={uniqueKey}
                sx={{ 
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    alignItems: 'flex-start'
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" color="textSecondary">
                            {timestamp}
                        </Typography>
                        <Chip
                            label={log.log_level}
                            size="small"
                            color={getLogLevelColor(log.log_level)}
                            sx={{ ml: 1 }}
                        />
                    </Box>
                    <Typography>{log.message}</Typography>
                </Box>
            </ListItem>
        );
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', minHeight: '100vh', paddingTop: '64px' }}>
                <NavigationDrawer
                    isMobile={isMobile}
                    isDrawerOpen={isDrawerOpen}
                    setIsDrawerOpen={setIsDrawerOpen}
                    activeSection={''}
                    setActiveSection={() => {}}
                    navigate={navigate}
                />
                <Box component="main" sx={{ flexGrow: 1, p: 3, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', paddingTop: '64px' }}>
            <NavigationDrawer
                isMobile={isMobile}
                isDrawerOpen={isDrawerOpen}
                setIsDrawerOpen={setIsDrawerOpen}
                activeSection={''}
                setActiveSection={() => {}}
                navigate={navigate}
            />
            <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                        <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
                            ETL Process Monitor
                        </Typography>
                        {isSystemAdmin && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => navigate('/admin/etl-operations')}
                            >
                                ETL Operations
                            </Button>
                        )}
                    </Box>
                    
                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}
                    {wsWarning && !error && (
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            {wsWarning}
                        </Alert>
                    )}

                    {/* Search Box */}
                    <Box sx={{ mb: 3 }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search logs..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm && (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleSearchClear} edge="end">
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>

                    {/* Results count */}
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Showing {filteredLogs.length} of {logs.length} logs
                    </Typography>

                    <List>
                        {filteredLogs.map((log, index) => renderLogEntry(log, index))}
                    </List>
                </Paper>
            </Box>
        </Box>
    );
}

export default ETLMonitoring; 